import { USER_LOAN_PROGRAM } from '@/config/constants';
import { downPaymentForProduct } from '@/utils/propertyDownPayment';
import { Product, ProductEsitmation } from '@/types/ProductType';

export type ProfitabilityAssumptions = {
  vacancyRatePercent: number;
  constructionCostPerSqft: number;
  holdingPeriodMonths: number;
};

export type MarketComparisonMetric = {
  id: string;
  label: string;
  value: number;
  display: string;
};

export type ProfitabilityMetrics = {
  program: USER_LOAN_PROGRAM;
  effectiveRent?: number;
  noi?: number;
  cashFlow?: number;
  dscr?: number;
  capRate?: number;
  cashOnCash?: number;
  rehabEstimate?: number;
  totalProjectCost?: number;
  profit?: number;
  profitMargin?: number;
  roi?: number;
  annualizedRoi?: number;
  monthlyInterestOnly?: number;
  totalInterest?: number;
  cashToClose?: number;
  totalCost?: number;
};

export const DEFAULT_PROFITABILITY_ASSUMPTIONS: ProfitabilityAssumptions = {
  vacancyRatePercent: 5,
  constructionCostPerSqft: 50,
  holdingPeriodMonths: 6,
};

const safeNumber = (value?: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const toPercent = (value?: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export const getInvestorProgram = (
  loanProgram?: string
): USER_LOAN_PROGRAM | null => {
  if (!loanProgram) return null;
  const normalized = loanProgram.toLowerCase();
  if (normalized === USER_LOAN_PROGRAM.DSCR) return USER_LOAN_PROGRAM.DSCR;
  if (normalized === USER_LOAN_PROGRAM.COMMERCIAL)
    return USER_LOAN_PROGRAM.COMMERCIAL;
  if (normalized === USER_LOAN_PROGRAM.FIX_AND_FLIP)
    return USER_LOAN_PROGRAM.FIX_AND_FLIP;
  if (normalized === USER_LOAN_PROGRAM.BRIDGE) return USER_LOAN_PROGRAM.BRIDGE;
  return null;
};

export const buildMarketComparisonMetrics = (
  product: Product,
  estimation: ProductEsitmation | null,
  assumptions: ProfitabilityAssumptions
): MarketComparisonMetric[] => {
  const purchasePrice = safeNumber(
    product?.loanInfo?.offerPrice || product.price
  );
  const sqft = safeNumber(product?.sqft);
  const rent = safeNumber(product?.loanInfo?.rent || (estimation as any)?.rent);
  const vacancyRate = toPercent(assumptions.vacancyRatePercent) / 100;
  const effectiveRent = rent * (1 - vacancyRate);
  const noi = effectiveRent * 12;
  const capRate = purchasePrice > 0 ? noi / purchasePrice : 0;
  const arv = safeNumber(product?.loanInfo?.arv);

  const metrics: MarketComparisonMetric[] = [
    {
      id: 'price_per_sqft',
      label: 'Price / Sqft',
      value: sqft > 0 ? purchasePrice / sqft : 0,
      display: sqft > 0 ? `$${(purchasePrice / sqft).toFixed(0)}` : 'N/A',
    },
    {
      id: 'rent_per_sqft',
      label: 'Rent / Sqft',
      value: sqft > 0 ? rent / sqft : 0,
      display: sqft > 0 ? `$${(rent / sqft).toFixed(2)}` : 'N/A',
    },
    {
      id: 'cap_rate',
      label: 'Cap Rate',
      value: capRate,
      display: capRate > 0 ? `${(capRate * 100).toFixed(2)}%` : 'N/A',
    },
    {
      id: 'arv_per_sqft',
      label: 'ARV / Sqft',
      value: sqft > 0 ? arv / sqft : 0,
      display: sqft > 0 && arv > 0 ? `$${(arv / sqft).toFixed(0)}` : 'N/A',
    },
  ];

  return metrics;
};

export const getProfitabilityMetrics = (
  product: Product,
  estimation: ProductEsitmation | null,
  assumptions: ProfitabilityAssumptions
): ProfitabilityMetrics | null => {
  if (!estimation) return null;
  const program = getInvestorProgram(
    (product?.savedLoanType as string) || product?.user?.loanProgram
  );
  if (!program) return null;

  const purchasePrice = safeNumber(
    product?.loanInfo?.offerPrice || product.price
  );
  const downPayment = safeNumber(downPaymentForProduct(product));
  const closingCosts = safeNumber((estimation as any)?.closingCosts);
  const cashToClose =
    safeNumber((estimation as any)?.cashToClose) ||
    safeNumber(downPayment + closingCosts);

  const vacancyRate = toPercent(assumptions.vacancyRatePercent) / 100;

  if (
    program === USER_LOAN_PROGRAM.DSCR ||
    program === USER_LOAN_PROGRAM.COMMERCIAL
  ) {
    const rent = safeNumber(
      product?.loanInfo?.rent || (estimation as any)?.rent
    );
    const effectiveRent = rent * (1 - vacancyRate);
    const monthlyPropertyTaxes = safeNumber(
      (estimation as any)?.monthlyPropertyTaxes ??
        product?.loanInfo?.monthlyPropertyTaxes
    );
    const monthlyHomeownersInsurance = safeNumber(
      (estimation as any)?.monthlyHomeownersInsurance ??
        product?.loanInfo?.monthlyHomeownersInsurance
    );
    const hoaFees = safeNumber(
      (estimation as any)?.hoaFees ?? product?.loanInfo?.hoaFees
    );
    const piti = safeNumber((estimation as any)?.piti);
    const debtService = piti + hoaFees;
    const noi =
      effectiveRent -
      (monthlyPropertyTaxes + monthlyHomeownersInsurance + hoaFees);
    const cashFlow = effectiveRent - debtService;
    const dscr = debtService > 0 ? effectiveRent / debtService : 0;
    const capRate = purchasePrice > 0 ? (noi * 12) / purchasePrice : 0;
    const cashOnCash = cashToClose > 0 ? (cashFlow * 12) / cashToClose : 0;

    return {
      program,
      effectiveRent,
      noi,
      cashFlow,
      dscr,
      capRate,
      cashOnCash,
    };
  }

  if (program === USER_LOAN_PROGRAM.FIX_AND_FLIP) {
    const rehab =
      safeNumber(product?.loanInfo?.rehab) ||
      safeNumber(product?.sqft) *
        safeNumber(assumptions.constructionCostPerSqft);
    const arv = safeNumber(product?.loanInfo?.arv);
    const totalProjectCost = purchasePrice + rehab + closingCosts;
    const profit = arv - totalProjectCost;
    const profitMargin = totalProjectCost > 0 ? profit / totalProjectCost : 0;
    const cashInvested = downPayment + rehab + closingCosts;
    const roi = cashInvested > 0 ? profit / cashInvested : 0;
    const annualizedRoi =
      assumptions.holdingPeriodMonths > 0
        ? roi * (12 / assumptions.holdingPeriodMonths)
        : 0;

    return {
      program,
      rehabEstimate: rehab,
      totalProjectCost,
      profit,
      profitMargin,
      roi,
      annualizedRoi,
    };
  }

  if (program === USER_LOAN_PROGRAM.BRIDGE) {
    const monthlyInterestOnly = safeNumber((estimation as any)?.monthlyPayment);
    const totalInterest = safeNumber((estimation as any)?.totalInterest);
    const totalCost = safeNumber((estimation as any)?.totalCost);

    return {
      program,
      monthlyInterestOnly,
      totalInterest,
      cashToClose,
      totalCost,
    };
  }

  return null;
};
